import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Badge, Button, Col, Container, Row, Spinner, Table, Form } from 'react-bootstrap';
import { useKeycloak } from '@react-keycloak/web';
import type Keycloak from 'keycloak-js';
import { confirmMatch, getMatching, MatchingResponse, MatchingReviewEntry, getRechte, addBlockiert, ErinnerungsBlock, Foerderndes, listErinnerungsBlocks, removeErinnerungsBlock, setErinnerungsBlock } from '../portal/ApiClient';

const ADMIN_ROLE = 'zuep-admin';

const STATUS_LABEL: Record<string, string> = {
  candidate: 'Mehrdeutig — Kandidaten vorhanden',
  unmatched: 'Kein Kandidat gefunden',
};

// Liest die Rolle direkt aus tokenParsed statt keycloak.hasRealmRole(), da der
// SKIP_AUTH-Mock (src/index.tsx) nur token/tokenParsed nachbildet, nicht die
// Keycloak-Instanzmethoden.
function hasAdminRole(keycloak: Keycloak): boolean {
  const roles: string[] = keycloak?.tokenParsed?.realm_access?.roles || [];
  return roles.includes(ADMIN_ROLE);
}

function MatchingReview() {
  const { keycloak } = useKeycloak();
  const [data, setData] = useState<MatchingResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pendingKey, setPendingKey] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  // Standard: nur die MEHRDEUTIGEN anzeigen (Name+Geburtsdatum reicht für
  // den Rest) -- die vielen Kein-Treffer-Fälle sind meist Mitglieder ohne ZÜP.
  const [showUnmatched, setShowUnmatched] = useState(false);
  const [blocks, setBlocks] = useState<ErinnerungsBlock[]>([]);
  const [foerdernde, setFoerdernde] = useState<Foerderndes[]>([]);
  const [weitereInaktive, setWeitereInaktive] = useState(0);
  const [neuBlockNr, setNeuBlockNr] = useState('');
  const [zeigeFoerdernde, setZeigeFoerdernde] = useState(false);

  useEffect(() => {
    let cancelled = false;
    listErinnerungsBlocks(keycloak.token)
      .then((r) => {
        if (!cancelled) {
          setBlocks(r.blocks);
          setFoerdernde(r.foerdernde || []);
          setWeitereInaktive(r.weitere_inaktive || 0);
        }
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keycloak.token]);

  // Admin-Rechte kommen aus dem Gateway (ADMIN_USERS/admin_user-DB) --
  // die Keycloak-Rolle ist nur noch ein Fallback (siehe /api/me/rechte).
  const [isAdmin, setIsAdmin] = useState(hasAdminRole(keycloak));
  useEffect(() => {
    let cancelled = false;
    getRechte(keycloak.token)
      .then((r) => {
        if (!cancelled && r.admin) setIsAdmin(true);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [keycloak.token]);

  const handleBlock = async (entry: MatchingReviewEntry) => {
    const grund = window.prompt(
      `„${[entry.vorname, entry.famname].filter(Boolean).join(' ')}" (${entry.mgd_nummer}) für ZÜP sperren?\nGrund:`,
      'operatives FO24-Konto',
    );
    if (grund === null) return;
    setPendingKey(entry.mgd_nummer);
    setActionError(null);
    try {
      await addBlockiert(keycloak.token, entry.mgd_nummer, grund || 'operatives FO24-Konto');
      setData((prev) => prev && {
        ...prev,
        blockiert: (prev.blockiert ?? 0) + 1,
        candidates: entry.status === 'candidate' ? Math.max(0, prev.candidates - 1) : prev.candidates,
        unmatched: entry.status === 'unmatched' ? Math.max(0, prev.unmatched - 1) : prev.unmatched,
        review: prev.review.filter((r) => r.mgd_nummer !== entry.mgd_nummer),
      });
    } catch (err) {
      setActionError('Sperren fehlgeschlagen: ' + (err as Error).message);
    } finally {
      setPendingKey(null);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getMatching(keycloak.token);
      setData(result);
    } catch (err) {
      setError('Matching-Übersicht konnte nicht geladen werden: ' + (err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [keycloak.token]);

  useEffect(() => {
    if (isAdmin) load();
    else setLoading(false);
  }, [isAdmin, load]);

  const handleConfirm = async (entry: MatchingReviewEntry, pId: number | null) => {
    setActionError(null);
    setPendingKey(entry.mgd_nummer);
    try {
      await confirmMatch(keycloak.token, entry.mgd_nummer, pId);
      // Bestätigten/verworfenen Fall aus der lokalen Review-Liste entfernen,
      // Counts entsprechend nachziehen, statt komplett neu zu laden.
      setData((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          candidates: entry.status === 'candidate' ? Math.max(0, prev.candidates - 1) : prev.candidates,
          unmatched: entry.status === 'unmatched' ? Math.max(0, prev.unmatched - 1) : prev.unmatched,
          review: prev.review.filter((r) => r.mgd_nummer !== entry.mgd_nummer),
        };
      });
    } catch (err) {
      setActionError(`Aktion für Mitglied ${entry.mgd_nummer} fehlgeschlagen: ` + (err as Error).message);
    } finally {
      setPendingKey(null);
    }
  };

  if (!isAdmin) {
    return (
      <Container fluid='md' className='py-4'>
        <Alert variant='warning'>
          Diese Ansicht ist nur für Administratoren (Rolle <code>{ADMIN_ROLE}</code>) verfügbar.
        </Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container fluid='md' className='py-4 text-center'>
        <Spinner animation='border' role='status' />
        <p className='mt-3'>Lade Matching-Übersicht…</p>
      </Container>
    );
  }

  if (error) {
    return (
      <Container fluid='md' className='py-4'>
        <Alert variant='danger'>{error}</Alert>
        <Button variant='outline-secondary' onClick={load}>Erneut versuchen</Button>
      </Container>
    );
  }

  if (!data) {
    return (
      <Container fluid='md' className='py-4'>
        <Alert variant='warning'>Keine Daten vorhanden.</Alert>
      </Container>
    );
  }

  return (
    <Container fluid='md' className='py-4'>
      <h2 className='mb-3'>Matching-Review · Mitglieder ↔ ZÜP-Personen</h2>

      <Row className='py-2 mb-3'>
        <Col md={3} xs={6} className='mb-2'>
          <div className='text-muted small'>Bereits verknüpft</div>
          <Badge bg='success' className='fs-6'>{data.verknuepft ?? 0}</Badge>
        </Col>
        <Col md={3} xs={6} className='mb-2'>
          <div className='text-muted small'>Neu automatisch verknüpft</div>
          <Badge bg='success' className='fs-6'>{data.auto}</Badge>
        </Col>
        <Col md={3} xs={6} className='mb-2'>
          <div className='text-muted small'>Mehrdeutig — bitte entscheiden</div>
          <Badge bg='warning' className='fs-6'>{data.candidates}</Badge>
        </Col>
        <Col md={3} xs={6} className='mb-2'>
          <div className='text-muted small'>Kein Treffer (meist ohne ZÜP)</div>
          <Badge bg='secondary' className='fs-6'>{data.unmatched}</Badge>
        </Col>
      </Row>

      <Form.Check
        type='switch'
        id='show-unmatched'
        className='mb-3'
        label='Auch Mitglieder ohne Treffer anzeigen (meist Mitglieder ohne ZÜP — normalerweise nichts zu tun)'
        checked={showUnmatched}
        onChange={(e) => setShowUnmatched(e.target.checked)}
      />

      {actionError && <Alert variant='danger'>{actionError}</Alert>}

      {(() => { return null; })()}
      {data.review.filter((r) => showUnmatched || r.status === 'candidate').length === 0 ? (
        <Alert variant='success'>Keine offenen Fälle — alle Mitglieder sind entweder automatisch verknüpft oder bereits entschieden.</Alert>
      ) : (
        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th>Mitglieds-Nr.</th>
              <th>Name</th>
              <th>Status</th>
              <th>Kandidaten (P-ID · Score · Name)</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {data.review.filter((r) => showUnmatched || r.status === 'candidate').map((entry) => (
              <tr key={entry.mgd_nummer}>
                <td>{entry.mgd_nummer}</td>
                <td>{[entry.vorname, entry.famname].filter(Boolean).join(' ')}</td>
                <td>{STATUS_LABEL[entry.status] || entry.status}</td>
                <td>
                  {entry.candidates.length === 0 ? (
                    <span className='text-muted'>—</span>
                  ) : (
                    <ul className='mb-0 ps-3'>
                      {entry.candidates.map((c) => (
                        <li key={c.p_id}>
                          P-ID {c.p_id} · Score {c.score} · {[c.vorname, c.famname].filter(Boolean).join(' ') || 'unbekannt'}
                          {' '}
                          <Button
                            variant='outline-success'
                            size='sm'
                            disabled={pendingKey === entry.mgd_nummer}
                            onClick={() => handleConfirm(entry, c.p_id)}
                          >
                            verknüpfen
                          </Button>
                        </li>
                      ))}
                    </ul>
                  )}
                </td>
                <td className='text-nowrap'>
                  <Button
                    variant='outline-danger'
                    size='sm'
                    className='me-1'
                    disabled={pendingKey === entry.mgd_nummer}
                    onClick={() => handleConfirm(entry, null)}
                  >
                    {pendingKey === entry.mgd_nummer ? 'Lädt…' : 'kein Treffer'}
                  </Button>
                  <Button
                    variant='outline-secondary'
                    size='sm'
                    title='Operatives FO24-Konto (keine echte Person): dauerhaft von ZÜP ausschließen'
                    disabled={pendingKey === entry.mgd_nummer}
                    onClick={() => handleBlock(entry)}
                  >
                    Für ZÜP sperren
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      <h4 className='mt-4 mb-2'>Erinnerungs-Blocks (keine Mails mehr)</h4>
      <p className='text-muted' style={{ fontSize: '0.85rem' }}>
        Diese Mitglieder haben sich von den automatischen Erinnerungs-Mails abgemeldet
        (z.B. „brauche keinen Flughafenausweis mehr"). Aufheben reaktiviert alle Erinnerungen.
      </p>
      {blocks.length === 0 ? (
        <p className='text-muted'>Keine Erinnerungs-Blocks.</p>
      ) : (
        <Table striped bordered size='sm' responsive style={{ maxWidth: 720 }}>
          <thead><tr><th>Mitglied</th><th>Notiz</th><th>seit</th><th></th></tr></thead>
          <tbody>
            {blocks.map((b) => (
              <tr key={`${b.typ}-${b.mgd_nummer}`}>
                <td>{b.name ? `${b.name} (${b.mgd_nummer})` : b.mgd_nummer}</td>
                <td style={{ fontSize: '0.85rem' }}>{b.notiz || '–'}</td>
                <td>{b.seit ? b.seit.slice(0, 10) : '–'}</td>
                <td>
                  {b.typ === 'optout' ? (
                    <Button size='sm' variant='outline-success' onClick={async () => {
                      try {
                        await removeErinnerungsBlock(keycloak.token, b.mgd_nummer);
                        setBlocks((prev) => prev.filter((x) => !(x.typ === 'optout' && x.mgd_nummer === b.mgd_nummer)));
                      } catch (err) {
                        setActionError('Aufheben fehlgeschlagen: ' + (err as Error).message);
                      }
                    }}>Aufheben</Button>
                  ) : (
                    <span className='text-muted' style={{ fontSize: '0.8rem' }}>automatisch</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
      <div className='d-flex gap-2 mb-4' style={{ maxWidth: 560 }}>
        <input type='text' className='form-control form-control-sm' style={{ maxWidth: 130 }}
          placeholder='Mitgliedsnr.' value={neuBlockNr} onChange={(e) => setNeuBlockNr(e.target.value)} />
        <Button size='sm' variant='outline-secondary' disabled={!neuBlockNr.trim()} onClick={async () => {
          try {
            await setErinnerungsBlock(keycloak.token, neuBlockNr.trim());
            const r = await listErinnerungsBlocks(keycloak.token);
            setBlocks(r.blocks);
            setNeuBlockNr('');
          } catch (err) {
            setActionError('Setzen fehlgeschlagen: ' + (err as Error).message);
          }
        }}>Erinnerungen stoppen</Button>
      </div>

      <h4 className='mt-4 mb-2'>Fördernde Mitglieder ({foerdernde.length})</h4>
      <p className='text-muted' style={{ fontSize: '0.85rem' }}>
        Fördernde werden erinnert, solange ihre ZÜP besteht oder ihr Ausweis vor weniger als
        einem Jahr abgelaufen ist — Alt-Fördernde ohne beides fallen automatisch aus den
        Erinnerungen ({weitereInaktive} Ausgeschiedene/Nicht-Mitglieder bekommen nie Post).
        Kein Handeln nötig.
        {' '}<Button size='sm' variant='link' className='p-0' onClick={() => setZeigeFoerdernde((v) => !v)}>
          {zeigeFoerdernde ? 'ausblenden' : 'anzeigen'}
        </Button>
      </p>
      {zeigeFoerdernde && (
        <Table striped bordered size='sm' responsive style={{ maxWidth: 560 }}>
          <thead><tr><th>Mitglied</th><th>Status</th></tr></thead>
          <tbody>
            {foerdernde.map((f) => (
              <tr key={f.mgd_nummer}>
                <td>{f.name ? `${f.name} (${f.mgd_nummer})` : f.mgd_nummer}</td>
                <td>{f.status}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </Container>
  );
}

export default MatchingReview;
